import { Button } from 'designSystem/components/Button'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'designSystem/components/ToggleGroup'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { SectionLabel } from 'features/timeSeries/sharedStyles'
import { isFiniteNumber } from 'features/timeSeries/utils/axisRangeHelpers'
import {
  AlignStartVertical,
  ChartLine,
  ChartScatter,
  Rows3,
} from 'lucide-react'
import React from 'react'
import styled from 'styled-components'
import { updateColorOpacity } from 'utils/colorHelpers'

/**
 * One control row: MODE toggle, Y-SCALE toggle, Auto. Y min/max editing lives
 * exclusively in the pens table below (editable in both scale modes), so the
 * bar's content is identical in both modes and toggling Single/Multi never
 * shifts the layout.
 */
const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.375rem 0.75rem;
  padding: 0.25rem 0.25rem 0.375rem;
  font-size: 0.6875rem;
`

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  white-space: nowrap;
`

/**
 * The shared ToggleGroup's default active state (bg-muted) is too subtle in
 * the dark theme, so the selected segment gets the primary-tinted fill +
 * primary ring the chart controls already use for "custom range active" (see
 * ActiveTimeRangeSectionWithPopover); unselected segments dim to secondary
 * text. Local override only — other ToggleGroup consumers keep the
 * design-system look.
 */
const SegmentItem = styled(ToggleGroupItem)`
  color: ${({ theme }) => theme.forecast.secondaryText};

  &:hover {
    color: ${({ theme }) => theme.forecast.defaultText};
  }

  &[data-state='on'] {
    background-color: ${({ theme }) =>
      updateColorOpacity(theme.forecast.primary ?? '#007BFF', 0.22)};
    box-shadow: inset 0 0 0 1px
      ${({ theme }) => theme.forecast.primary ?? '#007BFF'};
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

export const formatAutoRangeValue = (value: number): string =>
  Number(value.toPrecision(4)).toString()

/**
 * The placeholder an empty (auto) min/max field shows: the computed auto value
 * when one exists; a neutral dash while data is still loading; "No data" only
 * once loading has settled with no finite points.
 */
export const getAutoRangePlaceholder = (
  autoValue: number | undefined,
  rangesSettled: boolean
): string => {
  if (isFiniteNumber(autoValue)) {
    return formatAutoRangeValue(autoValue)
  }
  return rangesSettled ? 'No data' : '—'
}

/**
 * Scale-mode controls at the top of the pens table.
 *
 * Y min/max values are edited in the table's Y Min/Y Max columns in BOTH
 * modes. In single mode a committed cell edit fans out to every pen (see
 * applyAxisBoundEdit), so all rows updating together is the visual cue that
 * the scale is shared. The Auto button clears every custom range so all pens
 * auto-scale.
 */
export const YScaleControls: React.FC = () => {
  const {
    singleScaleMode,
    handleScaleModeToggle,
    autoScaleMode,
    handleAutoScaleAll,
    connectPoints,
    setConnectPoints,
  } = useScatterplotContext()

  return (
    <ControlsBar>
      <ControlGroup>
        <SectionLabel>Mode</SectionLabel>
        <ToggleGroup
          type='single'
          variant='outline'
          size='sm'
          aria-label='Chart mode'
          value={connectPoints ? 'line' : 'scatter'}
          onValueChange={(value) => {
            // Radix emits '' when the active segment is re-clicked; one mode
            // is always selected, so ignore deselection.
            if (value === 'scatter' || value === 'line') {
              setConnectPoints(value === 'line')
            }
          }}>
          <SegmentItem value='scatter'>
            <ChartScatter aria-hidden />
            Scatter
          </SegmentItem>
          <SegmentItem value='line'>
            <ChartLine aria-hidden />
            Line
          </SegmentItem>
        </ToggleGroup>
      </ControlGroup>
      <ControlGroup>
        <SectionLabel>Y-Scale</SectionLabel>
        <ToggleGroup
          type='single'
          variant='outline'
          size='sm'
          aria-label='Y-scale mode'
          value={singleScaleMode ? 'single' : 'multi'}
          onValueChange={(value) => {
            if (value === 'single' || value === 'multi') {
              handleScaleModeToggle(value === 'single')
            }
          }}>
          <SegmentItem value='single'>
            <AlignStartVertical aria-hidden />
            Single
          </SegmentItem>
          <SegmentItem value='multi'>
            <Rows3 aria-hidden />
            Multi
          </SegmentItem>
        </ToggleGroup>
      </ControlGroup>
      {/* Pinned to the row's right edge, clear of the toggles. The tooltip's
          wrapper span is the actual flex item, so the pin must live on it —
          a margin on the Button itself would be defeated by the wrapper. */}
      <ChartTooltip
        label='Clear all custom ranges and auto-scale every pen'
        side='top'
        style={{ marginLeft: 'auto' }}>
        <Button
          variant='outline'
          size='sm'
          disabled={autoScaleMode}
          onClick={handleAutoScaleAll}>
          Auto
        </Button>
      </ChartTooltip>
    </ControlsBar>
  )
}
